import { useState }   from "react";
import { Flag, Plus, Target, Trash2, Check, X } from "lucide-react";
import ProgressBar from "../shared/ProgressBar";

/**
 * CustomGoals
 * Allows the user to add arbitrary goals at runtime via an inline form.
 * Each goal gets a label, numeric target, unit string, and a live progress bar.
 *
 * Props:
 *   extraGoals  – array of { id, label, target, unit, current }
 *   addGoal     – fn({ label, target, unit })
 *   removeGoal  – fn(id)
 */
export default function CustomGoals({ extraGoals, addGoal, removeGoal }) {
  const [adding,  setAdding]  = useState(false);
  const [newGoal, setNewGoal] = useState({ label: "", target: "", unit: "" });

  function handleAdd() {
    addGoal(newGoal);
    setNewGoal({ label: "", target: "", unit: "" });
    setAdding(false);
  }

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">
          <Flag size={15} style={{ display: "inline", marginRight: 6 }} />
          Custom Goals
        </span>
        <button className="btn btn-secondary btn-sm" onClick={() => setAdding(a => !a)}>
          <Plus size={13} /> Add Goal
        </button>
      </div>

      <div className="card-body">

        {/* Add form */}
        {adding && (
          <div style={{
            display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap",
            marginBottom: 16, padding: "12px 14px",
            background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0",
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 140px" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Goal Label</label>
              <input
                className="form-input"
                placeholder="e.g. Referrals"
                value={newGoal.label}
                onChange={e => setNewGoal(g => ({ ...g, label: e.target.value }))}
                style={{ fontSize: 13 }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "0 0 100px" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Target #</label>
              <input
                className="form-input"
                type="number"
                placeholder="10"
                value={newGoal.target}
                onChange={e => setNewGoal(g => ({ ...g, target: Number(e.target.value) }))}
                style={{ fontSize: 13 }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "0 0 100px" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Unit</label>
              <input
                className="form-input"
                placeholder="calls, $, leads"
                value={newGoal.unit}
                onChange={e => setNewGoal(g => ({ ...g, unit: e.target.value }))}
                style={{ fontSize: 13 }}
              />
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn btn-primary btn-sm" onClick={handleAdd}><Check size={13} /> Add</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setAdding(false)}><X size={13} /></button>
            </div>
          </div>
        )}

        {extraGoals.length === 0 && !adding && (
          <p style={{ fontSize: 13, color: "#94a3b8", textAlign: "center", padding: "16px 0" }}>
            No custom goals yet. Click <strong>Add Goal</strong> to create one.
          </p>
        )}

        {extraGoals.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {extraGoals.map(eg => {
              const pct = eg.target > 0 ? Math.min(Math.round((eg.current / eg.target) * 100), 100) : 0;
              return (
                <div key={eg.id}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className="stat-icon blue" style={{ width: 34, height: 34, borderRadius: 8 }}>
                        <Target size={18} color="#2563eb" />
                      </div>
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>{eg.label}</div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>{eg.current} of {eg.target} {eg.unit}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: pct >= 100 ? "#16a34a" : "#0f172a" }}>
                        {pct}%
                      </span>
                      <button
                        onClick={() => removeGoal(eg.id)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", padding: 2 }}
                        title="Remove goal"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <ProgressBar current={eg.current} target={eg.target} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}